
import React, { useEffect, useMemo, useState } from "react";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaFileExcel,
  FaSearch,
} from "react-icons/fa";
import axiosInstance from "../api/axiosInstance";
import AddEditDepartmentMaster from "./AddEditDepartmentMaster";

const DepartmentMaster = () => {

  /* ================= USER ================= */
  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  const groupId = storedUser?.groupId;

  /* ================= STATE ================= */
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  /* ================= FETCH ================= */
  const fetchDepartments = async () => {
    if (!groupId) return;

    try {
      setLoading(true);

      const res = await axiosInstance.get(
        `/DepartmentMaster/GetByGroup?groupId=${groupId}`
      );

      setData(res.data || []);
    } catch (err) {
      console.error("Fetch department error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= LOAD ================= */
  useEffect(() => {
    fetchDepartments();
  }, []);

  /* ================= SEARCH ================= */
  const filteredData = useMemo(() => {
    const q = search.toLowerCase();

    return data.filter(
      (item) =>
        item.departmentName?.toLowerCase().includes(q) ||
        String(item.departmentId)?.includes(q)
    );
  }, [data, search]);

  /* ================= ACTIONS ================= */
  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this department?")) return;

    try {
      await axiosInstance.delete(
        `/DepartmentMaster/${row.departmentId}`
      );

      fetchDepartments();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  /* ================= EXCEL ================= */
  const exportExcel = () => {
    if (!filteredData.length) return;

    const rows = filteredData.map((r) => ({
      DepartmentId: r.departmentId,
      DepartmentName: `"${r.departmentName}"`,
      GroupId: r.groupId,
      Status: r.status ? "Active" : "Inactive",
      EntryDate: r.entryDate,
    }));

    const headers = Object.keys(rows[0]).join(",");

    const csv =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows.map((r) => Object.values(r).join(","))].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "DepartmentMaster.csv";
    link.click();
  };

  /* ================= UI ================= */
  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-5 flex flex-col border border-slate-200 min-h-[calc(100vh-120px)]">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">

        <h2 className="text-lg md:text-xl font-bold text-slate-800">
          Department Master
        </h2>

        <div className="flex flex-wrap gap-2">

          {/* SEARCH */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-slate-400 text-sm" />

            <input
              type="text"
              placeholder="Search department..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* EXCEL */}
          <button
            onClick={exportExcel}
            className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm"
          >
            <FaFileExcel />
            Excel
          </button>

          {/* ADD */}
          <button
            onClick={openAdd}
            className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm"
          >
            <FaPlus />
            Add
          </button>

        </div>
      </div>

      {/* ================= DESKTOP TABLE ================= */}
      <div className="hidden md:flex flex-1 min-h-0 border border-slate-300 rounded-lg overflow-hidden">
        <div className="w-full overflow-auto">

          <table className="w-full text-sm border-collapse">

            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="p-2 border text-left">Dept Id</th>
                <th className="p-2 border text-left">Department Name</th>
                <th className="p-2 border text-left">Group Id</th>
                <th className="p-2 border text-left">Status</th>
                <th className="p-2 border text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center p-6">
                    Loading...
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center p-6">
                    No data found
                  </td>
                </tr>
              ) : (
                filteredData.map((row) => (
                  <tr key={row.departmentId} className="hover:bg-slate-50">

                    <td className="p-2 border">{row.departmentId}</td>

                    <td className="p-2 border font-medium">
                      {row.departmentName}
                    </td>

                    <td className="p-2 border">{row.groupId}</td>

                    <td className="p-2 border">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          row.status
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {row.status ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="p-2 border text-center">
                      <div className="flex justify-center gap-3">

                        <FaEdit
                          onClick={() => openEdit(row)}
                          className="cursor-pointer text-blue-600"
                        />

                        <FaTrash
                          onClick={() => handleDelete(row)}
                          className="cursor-pointer text-red-600"
                        />

                      </div>
                    </td>

                  </tr>
                ))
              )}
            </tbody>

          </table>

        </div>
      </div>

      {/* ================= MOBILE VIEW ================= */}
      <div className="md:hidden flex-1 overflow-auto space-y-3">

        {loading ? (
          <div className="text-center py-6">Loading...</div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-6">No data found</div>
        ) : (
          filteredData.map((row) => (

            <div
              key={row.departmentId}
              className="border border-slate-200 rounded-xl p-4 shadow-sm"
            >

              <div className="flex justify-between items-center mb-2">

                <h3 className="font-semibold text-slate-800">
                  {row.departmentName}
                </h3>

                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    row.status
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {row.status ? "Active" : "Inactive"}
                </span>

              </div>

              <p className="text-sm text-slate-600">
                <b>Dept Id:</b> {row.departmentId}
              </p>

              <p className="text-sm text-slate-600 mb-3">
                <b>Group Id:</b> {row.groupId}
              </p>

              <div className="flex justify-end gap-4">

                <FaEdit
                  onClick={() => openEdit(row)}
                  className="cursor-pointer text-blue-600 text-lg"
                />

                <FaTrash
                  onClick={() => handleDelete(row)}
                  className="cursor-pointer text-red-600 text-lg"
                />

              </div>

            </div>

          ))
        )}

      </div>

      {/* MODAL */}
      {formOpen && (
        <AddEditDepartmentMaster
          editingData={editing}
          groupId={groupId}
          onClose={() => setFormOpen(false)}
          onSuccess={fetchDepartments}
        />
      )}

    </div>
  );
};

export default DepartmentMaster;
